import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, runTransaction, serverTimestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import { generateShareImages, generateDownloadImage, isMobileDevice, getShareCaption } from '../utils/shareImageGenerator';
import { analyticsService } from '../services/analyticsService';
import './TarotCard.css';

/**
 * TarotCard Component
 *
 * Displays quiz results as a flippable tarot card with:
 * - Front: Card number, title, image, tagline
 * - Back: Detailed personality insights, strengths, challenges
 * - 3D flip animation on click/tap
 * - Account creation modal for saving results
 */
const TarotCard = ({ cardData, userEmail, user }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isLoadingLikeState, setIsLoadingLikeState] = useState(true);
  const [isSaved, setIsSaved] = useState(false); // Used for download visual feedback
  const [shareImages, setShareImages] = useState({
    mobile: null,
    desktop: null,
    isGenerating: true
  });
  const [downloadImage, setDownloadImage] = useState(null);
  const hasAutoFlippedRef = useRef(false);

  // Auto-flip card after 5 seconds if not already flipped
  useEffect(() => {
    const autoFlipTimer = setTimeout(() => {
      if (!hasAutoFlippedRef.current) {
        console.log('🔄 Auto-flipping card after 5 seconds');
        hasAutoFlippedRef.current = true;
        setIsFlipped(true);
        // Track auto-flip
        if (cardData?.cardTitle) {
          analyticsService.trackTarotCardFlipped(cardData.cardTitle);
        }
      }
    }, 5000);

    return () => clearTimeout(autoFlipTimer);
  }, [cardData]); // Include cardData for tracking

  // Load user's like state and personality stats on component mount
  useEffect(() => {
    const loadLikeState = async () => {
      if (!cardData || !user) {
        setIsLoadingLikeState(false);
        return;
      }

      try {
        const personalityId = cardData.cardTitle.toLowerCase().replace(/\s+/g, '-');

        // Check if user has liked this personality
        const userLikeRef = doc(db, 'personality_stats', personalityId, 'likes', user.uid);
        const userLikeDoc = await getDoc(userLikeRef);

        if (userLikeDoc.exists()) {
          setIsLiked(true);
          console.log('💖 User has already liked this personality');
        }

        // Get current like count
        const statsRef = doc(db, 'personality_stats', personalityId);
        const statsDoc = await getDoc(statsRef);

        if (statsDoc.exists()) {
          const currentCount = statsDoc.data().like_count || 0;
          setLikeCount(currentCount);
          console.log('📊 Current like count:', currentCount);
        }

        setIsLoadingLikeState(false);
      } catch (error) {
        console.error('❌ Error loading like state:', error);
        setIsLoadingLikeState(false);
      }
    };

    loadLikeState();
  }, [cardData, user]);

  // Pre-generate share AND download images when component mounts
  useEffect(() => {
    if (cardData && cardData.cardImage) {
      console.log('🎨 Pre-generating share and download images...');

      // Generate both in parallel
      Promise.all([
        generateShareImages(cardData),
        generateDownloadImage(cardData)
      ])
        .then(([shareResult, downloadBlob]) => {
          setShareImages({
            mobile: shareResult.mobileBlob,
            desktop: shareResult.desktopBlob,
            isGenerating: false
          });
          setDownloadImage(downloadBlob);
          console.log('✅ Share and download images ready!');
        })
        .catch((error) => {
          console.error('❌ Error generating images:', error);
          setShareImages({
            mobile: null,
            desktop: null,
            isGenerating: false
          });
          setDownloadImage(null);
        });
    }
  }, [cardData]);

  const handleFlip = () => {
    // Track first manual flip (before auto-flip happens)
    if (!isFlipped && !hasAutoFlippedRef.current && cardData?.cardTitle) {
      hasAutoFlippedRef.current = true; // Mark as flipped to prevent duplicate tracking
      analyticsService.trackTarotCardFlipped(cardData.cardTitle);
    }
    setIsFlipped(!isFlipped);
  };

  const handleLike = async () => {
    // Prevent action while loading
    if (isLoadingLikeState || !user || !cardData) {
      return;
    }

    const personalityId = cardData.cardTitle.toLowerCase().replace(/\s+/g, '-');
    const statsRef = doc(db, 'personality_stats', personalityId);
    const userLikeRef = doc(db, 'personality_stats', personalityId, 'likes', user.uid);
    const userRef = doc(db, 'users', user.uid);

    // UNLIKE: User is removing their like
    if (isLiked) {
      try {
        // Optimistically update UI
        setIsLiked(false);
        setLikeCount(prevCount => Math.max(0, prevCount - 1));
        console.log('💔 Unlike button clicked, updating...');

        await runTransaction(db, async (transaction) => {
          // Get current stats
          const statsDoc = await transaction.get(statsRef);
          const currentCount = statsDoc.exists() ? (statsDoc.data().like_count || 0) : 0;
          const newCount = Math.max(0, currentCount - 1);

          // Update personality stats (decrement like count)
          transaction.set(statsRef, {
            like_count: newCount,
            display_name: cardData.cardTitle,
            card_number: cardData.cardNumber,
            last_updated: serverTimestamp()
          }, { merge: true });

          // Delete user's like document
          transaction.delete(userLikeRef);

          // Remove from user's liked personalities array
          transaction.update(userRef, {
            liked_personalities: arrayRemove(personalityId)
          });

          console.log('✅ Unlike transaction successful:', {
            personalityId,
            oldCount: currentCount,
            newCount
          });
        });

      } catch (error) {
        console.error('❌ Error unliking personality:', error);
        // Revert optimistic update
        setIsLiked(true);
        setLikeCount(prevCount => prevCount + 1);
        alert('Failed to unlike. Please try again.');
      }
    }
    // LIKE: User is adding their like
    else {
      try {
        // Optimistically update UI
        setIsLiked(true);
        setLikeCount(prevCount => prevCount + 1);
        console.log('💖 Like button clicked, updating...');

        // Track tarot card liked
        analyticsService.trackTarotCardLiked(cardData.cardTitle);

        await runTransaction(db, async (transaction) => {
          // Check if user has already liked (double-check in transaction)
          const userLikeDoc = await transaction.get(userLikeRef);
          if (userLikeDoc.exists()) {
            console.log('⚠️ User already liked this in transaction, aborting');
            throw new Error('Already liked');
          }

          // Get current stats
          const statsDoc = await transaction.get(statsRef);
          const currentCount = statsDoc.exists() ? (statsDoc.data().like_count || 0) : 0;
          const newCount = currentCount + 1;

          // Update personality stats
          transaction.set(statsRef, {
            like_count: newCount,
            display_name: cardData.cardTitle,
            card_number: cardData.cardNumber,
            last_updated: serverTimestamp()
          }, { merge: true });

          // Record user like in subcollection
          transaction.set(userLikeRef, {
            liked_at: serverTimestamp()
          });

          // Add to user's liked personalities array
          transaction.update(userRef, {
            liked_personalities: arrayUnion(personalityId)
          });

          console.log('✅ Like transaction successful:', {
            personalityId,
            oldCount: currentCount,
            newCount
          });
        });

      } catch (error) {
        // Revert optimistic update on error
        if (error.message === 'Already liked') {
          console.log('💬 Already liked, no action taken');
        } else {
          console.error('❌ Error liking personality:', error);
          setIsLiked(false);
          setLikeCount(prevCount => prevCount - 1);
          alert('Failed to like. Please try again.');
        }
      }
    }
  };

  const handleShare = async () => {
    // Track share initiated
    analyticsService.trackShareInitiated('native');

    // Check if images are still generating
    if (shareImages.isGenerating) {
      console.log('⏳ Share images still generating...');
      alert('Preparing your share image, please wait a moment...');
      return;
    }

    // Detect device type and select appropriate image
    const isMobile = isMobileDevice();
    const imageBlob = isMobile ? shareImages.mobile : shareImages.desktop;
    const caption = getShareCaption(cardData.cardTitle);
    const shareUrl = 'https://quiz.jaimee.ai';

    console.log('📤 Sharing:', { isMobile, hasImage: !!imageBlob, hasNavigatorShare: !!navigator.share });

    // Try Web Share API with file (Level 2) - best experience
    if (navigator.share && imageBlob) {
      const fileName = `jaimee-${cardData.cardTitle.toLowerCase().replace(/\s+/g, '-')}.png`;
      const file = new File([imageBlob], fileName, { type: 'image/png' });

      const shareDataWithFile = {
        title: `${cardData.cardTitle} - Jaimee Personality Quiz`,
        text: caption,
        files: [file]
      };

      // Check if sharing files is supported
      if (navigator.canShare && navigator.canShare(shareDataWithFile)) {
        try {
          await navigator.share(shareDataWithFile);
          console.log('✅ Shared successfully with image!');
          analyticsService.trackShareCompleted({ method: 'native', platform: 'with_image' });
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            console.log('Share cancelled by user');
            return;
          }
          console.log('File share failed, trying text share:', err.message);
        }
      }

      // Fallback: Try sharing without file (just text + URL)
      const shareDataTextOnly = {
        title: `${cardData.cardTitle} - Jaimee Personality Quiz`,
        text: caption,
        url: shareUrl
      };

      try {
        await navigator.share(shareDataTextOnly);
        console.log('✅ Shared successfully (text only)!');
        analyticsService.trackShareCompleted({ method: 'native', platform: 'text_only' });
        return;
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Share cancelled by user');
          return;
        }
        console.log('Text share also failed:', err.message);
      }
    }

    // Try basic Web Share (no files) if navigator.share exists but no image
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${cardData.cardTitle} - Jaimee Personality Quiz`,
          text: caption,
          url: shareUrl
        });
        console.log('✅ Shared successfully (basic share)!');
        analyticsService.trackShareCompleted({ method: 'native', platform: 'basic' });
        return;
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Share cancelled by user');
          return;
        }
        console.log('Basic share failed:', err.message);
      }
    }

    // Final fallback: Download image + copy caption
    if (imageBlob) {
      try {
        // Download image
        const url = URL.createObjectURL(imageBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `jaimee-${cardData.cardTitle.toLowerCase().replace(/\s+/g, '-')}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Copy caption to clipboard
        try {
          await navigator.clipboard.writeText(caption);
          alert('✅ Image downloaded and caption copied!\n\nPaste the caption when sharing your image on social media.');
        } catch (clipboardErr) {
          alert('✅ Image downloaded!\n\nShare it on social media with:\n\n' + caption);
        }

        console.log('✅ Downloaded image and copied caption');
        analyticsService.trackShareCompleted({ method: 'download', platform: 'fallback' });
      } catch (err) {
        console.error('Failed to download image:', err);
        alert('Unable to download image. Please try again.');
      }
    } else {
      // No image available - fallback to text only
      try {
        await navigator.clipboard.writeText(caption + '\n\n' + shareUrl);
        alert('✅ Share text copied to clipboard!');
        analyticsService.trackShareCompleted({ method: 'copy_link', platform: 'clipboard' });
      } catch (err) {
        console.error('Failed to copy:', err);
        alert('Share failed. Please try again.');
      }
    }
  };

  const handleDownload = async () => {
    // Track download initiated
    analyticsService.trackShareInitiated('download');

    // Check if download image is ready
    if (!downloadImage) {
      console.log('⏳ Download image still generating...');
      alert('Preparing your image, please wait a moment...');
      return;
    }

    try {
      // Download the long-form image with full summary and app store icons
      const url = URL.createObjectURL(downloadImage);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jaimee-${cardData.cardTitle.toLowerCase().replace(/\s+/g, '-')}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Show visual feedback
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);

      console.log('✅ Downloaded long-form image successfully');
      analyticsService.trackShareCompleted({ method: 'download', platform: 'direct' });
    } catch (err) {
      console.error('Failed to download image:', err);
      alert('Unable to download image. Please try again.');
    }
  };

  if (!cardData) {
    return (
      <div className="tarot-card-container">
        <div className="loading-message">
          Loading your results...
        </div>
      </div>
    );
  }

  return (
    <div className="tarot-card-container">
      {/* Jaimee Logo */}
      <img
        src="/assets/images/jaimee-logo-cream.svg"
        alt="Jaimee"
        className="jaimee-logo-cream"
      />

      {/* Wrapper to align card and buttons */}
      <div className="card-wrapper">
        {/* Tarot Card (clickable to flip) */}
        <div className={`tarot-card ${isFlipped ? 'flipped' : ''}`} onClick={handleFlip}>
          <div className="tarot-card-inner">
            {/* Front of card - Just the image */}
            <div className="card-face card-front">
              <div className="card-image-container">
                {cardData.cardImage ? (
                  <img
                    src={cardData.cardImage}
                    alt={cardData.cardTitle}
                    className="card-image"
                  />
                ) : (
                  <div className="card-image-placeholder">
                    <span className="placeholder-icon">✨</span>
                  </div>
                )}
              </div>
            </div>

            {/* Back of card */}
            <div className="card-face card-back" style={{ backgroundColor: cardData.backgroundColor }}>
              <div className="card-border">
                <div className="card-back-header">
                  <h3 className="card-back-title">{cardData.cardTitle}</h3>
                  <span className="card-number-back">{cardData.cardNumber}</span>
                </div>

                <div className="card-back-content-wrapper">
                  <div className="card-back-content">
                    {cardData.backContent?.summary && (
                      <div className="summary-section">
                        <p className="summary-text">{cardData.backContent.summary}</p>
                      </div>
                    )}
                  </div>
                  <div className="scroll-fade-indicator" />
                  <div className="scroll-glow-indicator" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons - Instagram style */}
        <div className="card-action-buttons">
          <button className="icon-button" onClick={handleFlip} aria-label="Flip card">
            <span className="material-icons">swipe</span>
          </button>

          <button
            className={`icon-button ${isLiked ? 'liked' : ''}`}
            onClick={handleLike}
            aria-label="Like"
            disabled={isLoadingLikeState}
          >
            <span className="material-icons">{isLiked ? 'favorite' : 'favorite_border'}</span>
            {likeCount > 0 && <span className="like-count">{likeCount}</span>}
          </button>

          <button className="icon-button" onClick={handleShare} aria-label="Share">
            <span className="material-icons">share</span>
          </button>

          <button
            className={`icon-button ${isSaved ? 'downloaded' : ''}`}
            onClick={handleDownload}
            aria-label="Download image"
          >
            <span className="material-icons">download</span>
          </button>
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <div className="scroll-line"></div>
          <div className="scroll-dot"></div>
        </div>

        {/* App Download CTA */}
        <div className="app-download-cta">
          <h3 className="cta-heading">Want to talk more?<br />Download the free app</h3>

          <div className="store-buttons">
            <a
              href="https://7degn.app.link/k6BpnB4B6Yb"
              target="_blank"
              rel="noopener noreferrer"
              className="store-button"
              aria-label="Download on the App Store"
            >
              <img
                src="/assets/images/app-store-badge.png"
                alt="Download on the App Store"
                className="store-badge-image"
              />
            </a>

            <a
              href="https://7degn.app.link/k6BpnB4B6Yb"
              target="_blank"
              rel="noopener noreferrer"
              className="store-button"
              aria-label="Get it on Google Play"
            >
              <img
                src="/assets/images/google-play-badge.png"
                alt="Get it on Google Play"
                className="store-badge-image"
              />
            </a>
          </div>

          {/* Jaimee logo with tagline */}
          <div
            className="jaimee-tagline-logo"
            role="img"
            aria-label="Jaimee - Let's talk about you"
            style={{
              WebkitMaskImage: 'url("/assets/images/Jaimee_with tagline_key black.svg")',
              maskImage: 'url("/assets/images/Jaimee_with tagline_key black.svg")'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TarotCard;
